import React, { useEffect, useMemo, useState } from 'react';
import { Input } from 'antd';
import { useTranslation } from 'react-i18next';
import Table from '@components/Table';
import { momentDateFormat } from '@utils/formatters';

import useSwr from '@hooks/useSwr';

import useSearchParams from '@hooks/useSearchParams';

const { Search } = Input;

const Applications = () => {
  const { t } = useTranslation(['applications']);

  const [applicationNameValue, setApplicationNameValue] = useState('');

  const {
    setSearchParams,
    hasValuesInExtraParams,
    clearSearchParams,
    searchParams,
  } = useSearchParams({
    type: 'pagination',
    extraParams: ['applicationName'],
  });

  const { applicationName, page, linesPerPage } = useMemo(
    () => searchParams,
    [searchParams],
  );

  useEffect(() => {
    setApplicationNameValue(applicationName);
  }, [applicationName]);

  const defaultParams = useMemo(
    () => ({
      applicationName,
    }),
    [applicationName],
  );

  const { data } = useSwr('/service-proxy/transactionTpdu/propertySetting', {
    params: {
      page: page ? Number(page) - 1 : 0,
      linesPerPage: linesPerPage ?? 20,
      ...defaultParams,
    },
  });

  const columns = useMemo(() => {
    const allColumns = [
      {
        title: t('table.activeServerIp'),
        dataIndex: 'activeServerIp',
        align: 'center' as const,
      },
      {
        title: t('table.activeServerPort'),
        dataIndex: 'activeServerPort',
        align: 'center' as const,
      },
      {
        title: t('table.applicationName'),
        dataIndex: 'applicationName',
        align: 'center' as const,
      },
      {
        title: t('table.applicationType'),
        dataIndex: 'applicationType',
        align: 'center' as const,
      },
      {
        title: t('table.createdAt'),
        dataIndex: 'createdAt',
        align: 'center' as const,
        render: (value: string) =>
          value && String(momentDateFormat(value, 'DD/MM/YYYY')),
      },
      {
        title: t('table.createdBy'),
        dataIndex: 'createdBy',
        align: 'center' as const,
      },

      {
        title: t('table.persistent'),
        dataIndex: 'persistentConnection',
        align: 'center' as const,
        render: (value: boolean) => (
          <>{value ? t('persistent.yes') : t('persistent.no')}</>
        ),
      },

      {
        title: t('table.standbyServerIp'),
        dataIndex: 'standbyServerIp',
        align: 'center' as const,
      },
      {
        title: t('table.standbyServerPort'),
        dataIndex: 'standbyServerPort',
        align: 'center' as const,
      },
      {
        title: t('table.status'),
        dataIndex: 'status',
        align: 'center' as const,
        render: (value: boolean) => (
          <>{value ? t('status.active') : t('status.inactive')}</>
        ),
      },
    ];

    return allColumns;
  }, [t]);

  const tableFilters = useMemo(
    () => [
      {
        visible: true,
        label: t('table.applicationName'),
        element: (
          <Search
            onSearch={value => setSearchParams({ applicationName: value })}
            onChange={({ target: { value } }) => setApplicationNameValue(value)}
            value={applicationNameValue}
            allowClear
          />
        ),
      },
    ],
    [applicationNameValue, setSearchParams, t],
  );

  return (
    <div>
      <Table
        columns={columns}
        data={data?.content || []}
        loading={!data}
        title={t('title')}
        tableKey="applications"
        filters={{
          list: tableFilters,
          hasValuesInFilters: hasValuesInExtraParams,
          onClearFilters: () => clearSearchParams(),
        }}
        onTableChange={setSearchParams}
        pagination={{
          page,
          linesPerPage,
          total: data?.totalElements,
        }}
      />
    </div>
  );
};

export default Applications;
